import { useEffect, useRef, useState } from 'react'

import {
  APPLIED,
  ASKED,
  EXPIRED,
  REFUSED,
  UNREACHABLE,
  actionSentence,
} from './watchRequests.js'

// V2-8a: the shared six-state action strip — HANDOFF.md's "Bot action
// states," `ActStrip.dc.html`'s own reference markup. Every write on the
// watch tab goes through `useWatchRequest()` (`watchRequests.js`); this
// component is the ONE shared presentation of whatever that hook reports,
// so `ProgressStepper`, `MemberActionRow`, `EpisodeTap` and (once V2-8b
// builds the redesigned rows) the card's own controls all read the same
// way — CLAUDE.md gotcha 1's *copies* axis is exactly the hazard a second,
// hand-rolled status renderer per caller would be.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 THIS COMPONENT NEVER ASKS THE BOT ANYTHING — it only RENDERS
//    `useWatchRequest()`'s own state. "Do not write a second request hook"
//    (the brief, verbatim): `Asked`/`Done`/`Refused`/`Expired`/`Unavailable`
//    below are read off `request.status`/`request.message`, never computed
//    here, and `Retry`/`OK` both call back into the CALLER's own `request`
//    object (`onRetry`, `request.reset`) rather than reimplementing polling.
// ═══════════════════════════════════════════════════════════════════════════
//
// Six states, and this component draws five of them (never `idle` — a live
// control with nothing pending — and never `signed out`, both of which are
// entirely the caller's own business: `ActStrip` renders NOTHING when
// `request.status` is falsy, so a caller that has not asked anything yet,
// or that never reaches this component at all because the viewer is signed
// out, is unaffected either way).
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 ASKED — A COUNTDOWN, AND WHAT HAPPENS AT ZERO (the brief's own question)
// ═══════════════════════════════════════════════════════════════════════════
//
// The applier polls every 20 seconds (`cogs/watch/requests.py::
// POLL_SECONDS`) and a request lives up to 300 seconds before the platform
// service lazily marks it expired (`ASKED_COUNTDOWN_SECONDS` below mirrors
// only the FIRST of those two numbers — the honest one to promise against,
// since 20s is the ordinary apply latency and 300s is a defensive outer
// bound nobody should expect to wait for). While the local elapsed time is
// under that mark, the strip counts UP ("Asking the bot… 0:07") with a bar
// filling toward it — deliberately framed as elapsed time, never a
// countdown TO the number, because a countdown that reaches zero claims a
// certainty ("it will be done by now") this page does not have. The moment
// elapsed time passes the mark, the label becomes **"Still asking…"** —
// one, honest, un-timed continuation, never a SECOND countdown toward the
// 300-second TTL (the brief, verbatim: *"a second countdown is not"*
// honest). It keeps reading "Still asking…" for as long as the request
// stays ASKED; nothing here ever gives up on the member's behalf — that is
// `useWatchRequest`'s own `MAX_WAIT_MS` (the poll's own defensive cap, not
// this component's job to duplicate).
//
// Elapsed time is tracked LOCALLY (`useAskedElapsed` below), not inside
// `useWatchRequest` itself — a purely presentational concern (this
// component's own countdown text) has no business growing the shared
// request hook's own state, and it resets cleanly every time `ask()` is
// called again (a fresh ASKED transition), including after a Retry.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 DONE — "THE NEW VALUE IN WORDS," NEVER FROM THE WIRE, NEVER BEFORE APPLIED
// ═══════════════════════════════════════════════════════════════════════════
//
// The platform service's own `applied` outcome carries no member-facing
// prose (`docs/reports/V2-3b.md` §1's outcome table: `{"detail": "applied"}`
// for a set-shaped method, nothing richer) — so "the new value in words"
// cannot come from the wire without a backend change this slice does not
// make (SITE-ONLY, no bot code). Every caller instead builds `doneText`
// from what it ASKED for — the exact target it already holds to build the
// request body in the first place — and passes it in as a prop. Because
// this text is not rendered until `request.status === APPLIED` (i.e.,
// after the bot has genuinely confirmed it), showing "what we asked for" at
// that point is showing the truth, never an optimistic guess — the brief's
// own closing rule, "never show the new value before the bot confirms it,"
// is met by WHEN this renders, not by where the words come from.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 REFUSED / EXPIRED — NEITHER IS A DEAD END (RECONCILIATION §11 + the
//    prototype's own `onDismiss`/`onRetry`)
// ═══════════════════════════════════════════════════════════════════════════
//
// Before this component, NOTHING reset `request.status` after a refusal or
// an expiry — `useWatchRequest`'s own `reset()` was only ever called after
// a confirmed APPLIED (`useConfirmAfterApplied`, `WatchPanel.jsx`), so a
// refused or expired control stayed stuck on that one sentence until the
// member navigated away and back. RECONCILIATION §11 explicitly adds "a
// Retry on expired"; the prototype's own `ActStrip.dc.html` draws an "OK"
// on refused too (`act.onDismiss`), and this component builds both: OK
// simply calls `request.reset()` (a purely local UI reset — no request is
// sent), and Retry calls the caller's own `onRetry`, which re-invokes
// `request.ask(sameBody)` — a genuinely NEW request (a fresh `client_key`,
// minted inside `ask()`), never a resend of the dead one, per the brief's
// own words for Expired.
//
// ⚠️ BOTH BUTTONS ARE OMITTED WHEN THE CALLER HAS NO HANDLER TO OFFER, AND
// THIS IS LOAD-BEARING FOR `EpisodeTap` (`WatchTitle.jsx`) SPECIFICALLY —
// P6-283's own row IS a `<button>` (the whole aired episode row), and a
// `<button>` nested inside a `<button>` is invalid content, the identical
// hazard that file's own header already names for an `<a>` inside a
// `<button>`. `EpisodeTap` passes neither `onRetry` nor a `request.reset`
// (its proxy `request` object omits `reset` entirely), because the OUTER
// row already re-sends the action when tapped again — `disabled` is only
// `true` while genuinely ASKED, so a refused-or-expired row is still a
// live, clickable button, and pressing it IS the retry. Rendering a SECOND,
// nested button there would be both invalid and redundant.

/**
 * P6-281 §1.1, MOVED HERE FROM `WatchPanel.jsx` IN V2-8a (unchanged in
 * behaviour — see the module header for why): confirm an APPLIED request
 * against a FRESH read before resetting the control back to its live
 * state, rather than trusting a single unconditional refresh — shared by
 * `ProgressStepper`, `MemberActionRow` and `WatchTitle.jsx`'s episode-tap
 * controller (CLAUDE.md gotcha 1's *copies* axis: two hand-rolled
 * confirmation effects are two chances to drift).
 *
 * `onChanged` is `watch.confirm` / `title.confirm` (`watch.js` /
 * `watchTitle.js`) — the bounded re-read loop. `isConfirmed(shared)` is
 * rebuilt every render from whatever THIS press actually asked for (a
 * plain function, not memoised — the effect below only re-runs on a
 * `request.status` transition, so it captures the `isConfirmed` in scope
 * at THAT render, which is the one where the press just happened).
 *
 * Returns `confirmFailed`: `null` before anything is pressed or while
 * still checking, `false` once confirmed, `true` if the bound ran out
 * without ever seeing the change on a fresh read. The caller renders
 * that last one as an honest, small addendum — never as a claim that the
 * press itself failed, because it did not: the request already reads
 * APPLIED before this even starts checking.
 *
 * The reset timer starts only AFTER confirmation settles, never in
 * parallel with it — confirming can itself take a few seconds, and
 * resetting back to the live control mid-check would race the very
 * thing meant to prove that control is safe to show again.
 */
export function useConfirmAfterApplied(request, onChanged, isConfirmed) {
  const [confirmFailed, setConfirmFailed] = useState(null)

  useEffect(() => {
    if (request.status !== APPLIED) return undefined
    let cancelled = false
    let resetTimer = null
    setConfirmFailed(null)
    Promise.resolve(onChanged?.(isConfirmed)).then((confirmed) => {
      if (cancelled) return
      setConfirmFailed(confirmed === false)
      resetTimer = window.setTimeout(() => request.reset(), 1500)
    })
    return () => {
      cancelled = true
      if (resetTimer !== null) window.clearTimeout(resetTimer)
    }
    // `isConfirmed` is deliberately NOT a dependency — it is a fresh
    // closure every render, and this effect must fire exactly once per
    // APPLIED transition (the same "request.reset is stable, re-running
    // this on every render would restart the timer" reasoning this file
    // already applied before P6-281).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request.status, onChanged])

  return confirmFailed
}

/** The small, honest addendum `useConfirmAfterApplied` asks every caller
 * to render — never inline text, so the wording cannot drift between the
 * stepper, the status control and the episode-tap controller. MOVED HERE
 * FROM `WatchPanel.jsx` IN V2-8a, alongside `useConfirmAfterApplied`
 * immediately above, for the identical reason — one more caller (this
 * file's own Done state), one more chance for the wording to drift if it
 * were retyped instead. */
export function ConfirmStaleNote({ testId }) {
  return (
    <span className="d2d-micro" data-testid={testId}>
      {' '}
      — may take a moment to show up here
    </span>
  )
}

/** The applier's own poll cadence (`cogs/watch/requests.py::POLL_SECONDS`),
 * mirrored here as the point past which this strip stops counting up and
 * starts reading "Still asking…" — see the header for why this is the
 * honest number to frame against, not the platform service's 300s TTL. */
export const ASKED_COUNTDOWN_SECONDS = 20

function pad2(n) {
  return String(n).padStart(2, '0')
}

/** `0:07`, `1:04` — minutes:seconds from whole elapsed seconds. */
function elapsedLabel(totalSeconds) {
  const m = Math.floor(totalSeconds / 60)
  const s = totalSeconds % 60
  return `${m}:${pad2(s)}`
}

/**
 * Elapsed whole seconds since `active` most recently became `true`, ticking
 * once a second while it stays `true`, reset to `0` the instant it flips
 * back on. Purely local, presentational timing — see the module header for
 * why this does not live inside `useWatchRequest`.
 */
function useAskedElapsed(active) {
  const [elapsed, setElapsed] = useState(0)
  const startRef = useRef(null)

  useEffect(() => {
    if (!active) {
      startRef.current = null
      return undefined
    }
    startRef.current = Date.now()
    setElapsed(0)
    const timer = window.setInterval(() => {
      setElapsed(Math.floor((Date.now() - startRef.current) / 1000))
    }, 1000)
    return () => window.clearInterval(timer)
  }, [active])

  return elapsed
}

/**
 * The shared six-state action strip.
 *
 * `request` is `useWatchRequest()`'s own return value (`{status, message,
 * busy}`) — never a copy of it. `doneText` (APPLIED) and `command`
 * (UNREACHABLE, mono fallback — "reuse the existing named-parameter
 * command builder," the brief's own words: pass `progressCommand(...)` or
 * the caller's own status-command builder) are the caller's own honest
 * strings; both may be omitted, and the strip degrades to the plain
 * `actionSentence` text `watchRequests.js` already defines for exactly that
 * case. `onRetry` re-sends the SAME action as a NEW request (Expired only);
 * omitting it simply drops the button, never a broken one.
 */
export function ActStrip({
  request,
  confirmFailed,
  testId,
  confirmStaleTestId,
  doneText,
  command,
  onRetry,
}) {
  const elapsed = useAskedElapsed(request.status === ASKED)

  if (!request.status) return null

  if (request.status === ASKED) {
    const stillWaiting = elapsed >= ASKED_COUNTDOWN_SECONDS
    const pct = stillWaiting ? 100 : Math.round((elapsed / ASKED_COUNTDOWN_SECONDS) * 100)
    return (
      <div className="d2d-actstrip d2d-actstrip--asked" role="status" data-testid={testId} data-state={ASKED}>
        <div className="d2d-actstrip__row">
          <span className="d2d-micro d2d-actstrip__kicker">
            {stillWaiting ? 'Still asking…' : 'Asking the bot…'}
          </span>
          <span className="d2d-actstrip__elapsed">{elapsedLabel(elapsed)}</span>
        </div>
        <div className="d2d-actstrip__bar">
          <div className="d2d-actstrip__barfill" style={{ width: `${pct}%` }} />
        </div>
      </div>
    )
  }

  if (request.status === APPLIED) {
    return (
      <div className="d2d-actstrip d2d-actstrip--done" role="status" data-testid={testId} data-state={APPLIED}>
        <span className="d2d-micro d2d-actstrip__kicker">Done ✓</span>
        <span className="d2d-actstrip__text">{doneText ?? actionSentence(APPLIED, null)}</span>
        {confirmFailed ? (
          <ConfirmStaleNote testId={confirmStaleTestId ?? `${testId}-confirm-stale`} />
        ) : null}
      </div>
    )
  }

  if (request.status === REFUSED) {
    // HANDOFF.md and the brief both quote this state verbatim as "Refused ·
    // <reason in plain words>" — ONE line, the applier's own sentence shown
    // exactly as written, never re-worded here. (`ActStrip.dc.html`'s own
    // markup instead puts the ACTION's label after "Refused ·" and the
    // reason on a line of its own; the two governing docs agree with each
    // other and differ from the prototype on this one point, so the docs
    // win — see docs/reports/V2-8a.md.)
    return (
      <div className="d2d-actstrip d2d-actstrip--refused" role="alert" data-testid={testId} data-state={REFUSED}>
        <div className="d2d-actstrip__body">
          <div className="d2d-actstrip__text">
            <span className="d2d-micro d2d-actstrip__kicker">Refused</span>
            {' · '}
            {actionSentence(REFUSED, request.message)}
          </div>
          <div className="d2d-micro d2d-actstrip__note">Nothing changed.</div>
        </div>
        {typeof request.reset === 'function' ? (
          <button
            type="button"
            className="d2d-actstrip__okbtn"
            data-testid={`${testId}-ok`}
            onClick={request.reset}
          >
            OK
          </button>
        ) : null}
      </div>
    )
  }

  if (request.status === EXPIRED) {
    return (
      <div className="d2d-actstrip d2d-actstrip--expired" role="alert" data-testid={testId} data-state={EXPIRED}>
        <div className="d2d-actstrip__body">
          <div className="d2d-micro d2d-actstrip__kicker">Expired</div>
          <div className="d2d-actstrip__text">{actionSentence(EXPIRED, null)}</div>
        </div>
        {onRetry ? (
          <button
            type="button"
            className="d2d-actstrip__retrybtn"
            data-testid={`${testId}-retry`}
            onClick={onRetry}
          >
            Try again
          </button>
        ) : null}
      </div>
    )
  }

  // UNREACHABLE — a plain-command fallback when the caller has an honest
  // one to offer; otherwise the same generic sentence this state has
  // always shown.
  if (request.status === UNREACHABLE && command) {
    return (
      <div
        className="d2d-actstrip d2d-actstrip--unavailable"
        data-testid={testId}
        data-state={UNREACHABLE}
      >
        <div className="d2d-micro d2d-actstrip__kicker">Buttons are unavailable</div>
        <div className="d2d-actstrip__text">
          /watch in Discord still works — <code className="d2d-actstrip__cmd">{command}</code>
        </div>
      </div>
    )
  }

  return (
    <span className="d2d-actstrip d2d-actstrip--plain" data-testid={testId} data-state={request.status}>
      {actionSentence(request.status, request.message)}
    </span>
  )
}

export default ActStrip
